import { useContext, useEffect } from 'react'
import { useLocation } from 'react-router-dom'

import ModalContext from 'contexts/ModalContext'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { SET_SHOW_LUX_PLUS_FREE_PREVIEW_MODAL } from 'actions/actionConstants'
import { get as getExpirable, remove as removeExpirable, set as setExpirable } from 'lib/storage/expirableLocalStorage'
import LuxPlusFreePreviewUnlockedModal from 'luxPlus/components/modals/LuxPlusFreePreviewUnlockedModal'
import useTierUpgradeCampaigns from 'loyalty/hooks/useTierUpgradeCampaigns'
import { isTierUpgradeCampaignPath } from 'loyalty/tierUpgradeCampaign/tierUpgradeCampaignRoutes'

export const FREE_PREVIEW_MODAL_PENDING_KEY = 'luxPlusFreePreviewUnlockedModalPending'
const FREE_PREVIEW_MODAL_PENDING_TTL_SECONDS = 7 * 24 * 60 * 60

interface PendingFreePreviewModal {
  memberId: string
  trigger?: App.MemberSubscriptionItem['trigger']
}

/**
 * Opens the "you've unlocked a free preview" celebration modal once a customer has been enrolled
 * into the LuxPlus+ Free Preview (createFreePreviewMember sets the one-shot flag). The flag is
 * cleared immediately so the modal only shows once. Replaces the old success snackbar.
 *
 * The trigger is read before clearing and passed down rather than selected inside the modal —
 * clearing the flag wipes it from the store on the same tick the modal mounts.
 *
 * It waits while the customer is on a Société tier upgrade campaign landing page, which runs its own
 * sign-up and claim modals. The wait is kept in local storage for the member, so the modal still
 * opens on the next other page after a reload or in another tab (the verification email link opens
 * a new one), and only once across tabs.
 */
function useShowLuxPlusFreePreviewUnlockedModal() {
  const showModal = useContext(ModalContext)
  const dispatch = useAppDispatch()
  const shouldShow = useAppSelector(state => state.luxPlus.showFreePreviewEnrolledModal ?? false)
  const trigger = useAppSelector(state => state.luxPlus.freePreviewEnrolledTrigger)
  const memberId = useAppSelector(state => state.auth.account.memberId)
  const { pathname } = useLocation()
  const tierUpgradeCampaigns = useTierUpgradeCampaigns()
  const isOnTierUpgradeCampaignPage = tierUpgradeCampaigns.some(campaign => isTierUpgradeCampaignPath(pathname, campaign.slug))

  useEffect(() => {
    if (shouldShow) {
      dispatch({ type: SET_SHOW_LUX_PLUS_FREE_PREVIEW_MODAL, show: false })
      if (isOnTierUpgradeCampaignPage) {
        if (memberId) {
          setExpirable<PendingFreePreviewModal>(FREE_PREVIEW_MODAL_PENDING_KEY, { memberId, trigger }, FREE_PREVIEW_MODAL_PENDING_TTL_SECONDS)
        }
        return
      }
      removeExpirable(FREE_PREVIEW_MODAL_PENDING_KEY)
      showModal(<LuxPlusFreePreviewUnlockedModal trigger={trigger} />)
      return
    }

    if (isOnTierUpgradeCampaignPage || !memberId) return
    const pending = getExpirable<PendingFreePreviewModal>(FREE_PREVIEW_MODAL_PENDING_KEY)
    if (pending?.memberId !== memberId) return
    removeExpirable(FREE_PREVIEW_MODAL_PENDING_KEY)
    showModal(<LuxPlusFreePreviewUnlockedModal trigger={pending.trigger} />)
  }, [shouldShow, isOnTierUpgradeCampaignPage, memberId, trigger, dispatch, showModal])
}

export default useShowLuxPlusFreePreviewUnlockedModal
